import { AuthLayout } from '@/bundle/Auth/ui/AuthLayout/AuthLayout';
import { BillingFrequencyType, BILLING_FREQUENCY_DB, BILLING_FREQUENCY_LABEL } from '@/const/shared';
import { useState } from 'react';
import { BillingFrequencyToggle } from '@/bundle/Auth/AdminOnboardingFlow/SelectBillingPlanPage/ui/BillingFrequencyToggle/BillingFrequencyToggle';
import { PricingPlanCard } from '@/bundle/Auth/AdminOnboardingFlow/SelectBillingPlanPage/ui/PricingPlanCard/PricingPlanCard';
import { Box } from '@/components/Box/Box';
import { AuthHeading } from '@/bundle/Auth/ui/AuthHeading/AuthHeading';
import { useQuery } from '@tanstack/react-query';
import { getBillingPlans } from './api';
import { LayoutSpinner } from '@/bundle/Layouts/styles';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { Notification } from '@/components/Notification/Notification';
import { getFormattedBillingPlans } from './helpers';
import { LogoutLink } from '@/bundle/Auth/AdminOnboardingFlow/SelectBillingPlanPage/ui/LogoutLink';
import { DowngradePlanPreviewModal } from './ui/DowngradePlanPreviewModal';
import { createSearchParams, useNavigate } from 'react-router';
import { getSubscriptionCheckoutPath } from '../SubscriptionCheckoutPage/path';
import { useSubscriptionStatus } from '@/bundle/shared/hooks/useSubscriptionStatus';
import { getBillingCurrentPlan } from '@/helpers/billingHelpers';
import { useUser } from '@/context/userContext';
import { BillingPlanType } from '@/api/v1/billing/getBillingPlanDetailsApi';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { AdminOnboardingStepper } from '../ui/AdminOnboardingStepper';
import { getAdminOnboardingFlow } from '@/bundle/shared/hooks/useAdminOnboardingFlowFromAuthToken';

export const SelectBillingPlanPage = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { isCancelledSubscription } = useSubscriptionStatus();

  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [downgradeModalOpen, setDowngradeModalOpen] = useState(false);
  const [billingFrequency, setBillingFrequency] = useState<BillingFrequencyType>(BILLING_FREQUENCY_DB.MONTH);

  const { data, isPending } = useQuery({
    queryKey: ['get_billing_plans'],
    queryFn: () => getBillingPlans(),
  });

  const navigateToCheckout = (planId: string) => {
    navigate({
      pathname: getSubscriptionCheckoutPath(),
      search: createSearchParams({ plan_id: planId }).toString(),
    });

    setSelectedPlanId(null);
  };

  const closeDowngradeModal = () => {
    setDowngradeModalOpen(false);
    setSelectedPlanId(null);
  };

  const selectPlan = (planDetails: BillingPlanType) => {
    const { id, monthly_views, name, billing_frequency } = planDetails;
    const currentPlanMonthlyViews = getBillingCurrentPlan(user)?.monthly_views;

    setSelectedPlanId(id);
    amplitudeService.logEvent(AMPLITUDE_EVENTS.SelectBillingPlan, {
      plan_type: name,
      frequency: BILLING_FREQUENCY_LABEL[billing_frequency],
    });

    // Show downgrade modal for cancelled subscription and plan with lower limit plan
    if (isCancelledSubscription && currentPlanMonthlyViews > monthly_views) {
      setDowngradeModalOpen(true);

      return;
    }

    navigateToCheckout(id);
  };

  const plans = getFormattedBillingPlans(data?.body?.results);
  const hasPlans = !!plans.length;
  const error = getResponseError(data?.error);
  const { isOnboardingFlow, signUpStep } = getAdminOnboardingFlow(user);

  return (
    <>
      <AuthLayout position='top' banner={isOnboardingFlow && <AdminOnboardingStepper signUpStep={signUpStep} />}>
        <AuthHeading header='Find the plan for you' subHeader='You can change it anytime.' />

        {error && (
          <Notification variant='error' marginBottom='16px'>
            {error}
          </Notification>
        )}
        {isPending && <LayoutSpinner />}

        {hasPlans && (
          <>
            <Box marginBottom='42px'>
              <BillingFrequencyToggle frequency={billingFrequency} onChange={setBillingFrequency} />
            </Box>

            <Box
              display='flex'
              alignItems='center'
              justifyContent='center'
              padding='0 16px'
              rowGap='24px'
              columnGap='24px'
              flexWrap='wrap'
            >
              {plans.map((plan) => {
                return (
                  <PricingPlanCard
                    key={plan?.name}
                    plan={plan}
                    billingFrequency={billingFrequency}
                    onSelect={selectPlan}
                  />
                );
              })}
            </Box>
          </>
        )}
        <LogoutLink />
      </AuthLayout>
      <DowngradePlanPreviewModal
        isOpen={downgradeModalOpen}
        onCancel={closeDowngradeModal}
        onConfirm={() => navigateToCheckout(selectedPlanId)}
      />
    </>
  );
};
